import { useEffect, useRef, memo } from "react";
import { createChart, CandlestickSeries, HistogramSeries, LineSeries } from "lightweight-charts";

/**
 * BLOFIN-style candlestick chart powered by TradingView's lightweight-charts.
 * Renders candles + volume histogram + entry / SL / TP horizontal lines + signal markers.
 *
 * Props:
 *  - candles: array of { time (iso), open, high, low, close, volume }
 *  - direction: "long" | "short"
 *  - entry, stop_loss, take_profit_1, take_profit_2 (numbers)
 *  - markers: array of { time (iso), direction, score } (extra dots — optional)
 *  - height: number
 */
export const TradingChart = memo(function TradingChart({
  candles = [],
  direction = "long",
  entry,
  stop_loss,
  take_profit_1,
  take_profit_2,
  markers = [],
  height = 360,
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const volSeriesRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      localization: {
        locale: "en-US",
        dateFormat: "yyyy-MM-dd",
      },
      layout: {
        background: { color: "#0A0A0A" },
        textColor: "#e5e5e5",
        fontFamily: "JetBrains Mono, monospace",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.04)" },
        horzLines: { color: "rgba(255,255,255,0.04)" },
      },
      rightPriceScale: {
        borderColor: "rgba(255,255,255,0.1)",
        scaleMargins: { top: 0.08, bottom: 0.28 },
      },
      timeScale: {
        borderColor: "rgba(255,255,255,0.1)",
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time) => {
          const d = new Date(time * 1000);
          const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
          const dd = String(d.getUTCDate()).padStart(2, "0");
          const hh = String(d.getUTCHours()).padStart(2, "0");
          const mi = String(d.getUTCMinutes()).padStart(2, "0");
          return `${mm}-${dd} ${hh}:${mi}`;
        },
      },
      crosshair: {
        mode: 1,
        vertLine: { color: "rgba(0,255,102,0.4)", labelBackgroundColor: "#00FF66" },
        horzLine: { color: "rgba(0,255,102,0.4)", labelBackgroundColor: "#00FF66" },
      },
      autoSize: true,
      handleScroll: true,
      handleScale: true,
    });
    chartRef.current = chart;

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#00FF66",
      downColor: "#FF3B30",
      borderUpColor: "#00FF66",
      borderDownColor: "#FF3B30",
      wickUpColor: "#00FF66",
      wickDownColor: "#FF3B30",
      priceFormat: { type: "price", precision: 6, minMove: 0.000001 },
    });
    candleSeriesRef.current = candleSeries;

    const volSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "vol",
      color: "rgba(255,255,255,0.35)",
    });
    chart.priceScale("vol").applyOptions({
      scaleMargins: { top: 0.75, bottom: 0 },
    });
    volSeriesRef.current = volSeries;

    const ro = new ResizeObserver(() => chart.applyOptions({ autoSize: true }));
    ro.observe(containerRef.current);
    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
    };
  }, []);

  // Update data
  useEffect(() => {
    if (!candleSeriesRef.current || !volSeriesRef.current) return;
    if (!candles || candles.length === 0) return;
    const cData = candles.map((c) => ({
      time: Math.floor(new Date(c.time).getTime() / 1000),
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    }));
    const vData = candles.map((c) => ({
      time: Math.floor(new Date(c.time).getTime() / 1000),
      value: c.volume,
      color: c.close >= c.open ? "rgba(0,255,102,0.35)" : "rgba(255,59,48,0.35)",
    }));
    // dynamic price precision based on last close
    const lastClose = candles[candles.length - 1].close;
    let precision = 4;
    let minMove = 0.0001;
    if (lastClose > 0 && lastClose < 1) {
      precision = Math.max(4, Math.ceil(-Math.log10(lastClose)) + 4);
      minMove = Math.pow(10, -precision);
    } else if (lastClose >= 1000) {
      precision = 2;
      minMove = 0.01;
    }
    candleSeriesRef.current.applyOptions({
      priceFormat: { type: "price", precision, minMove },
    });
    candleSeriesRef.current.setData(cData);
    volSeriesRef.current.setData(vData);
    chartRef.current?.timeScale().fitContent();
  }, [candles]);

  // Update price lines (entry / SL / TP)
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series) return;
    const lines = [];
    const accent = direction === "short" ? "#FF3B30" : "#00FF66";
    if (entry !== undefined && entry !== null) {
      lines.push(
        series.createPriceLine({
          price: entry,
          color: "#FFFFFF",
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: true,
          title: "ENTRY",
        }),
      );
    }
    if (stop_loss !== undefined && stop_loss !== null) {
      lines.push(
        series.createPriceLine({
          price: stop_loss,
          color: "#FF3B30",
          lineWidth: 1,
          lineStyle: 3,
          axisLabelVisible: true,
          title: "SL",
        }),
      );
    }
    if (take_profit_1 !== undefined && take_profit_1 !== null) {
      lines.push(
        series.createPriceLine({
          price: take_profit_1,
          color: accent,
          lineWidth: 1,
          lineStyle: 3,
          axisLabelVisible: true,
          title: "TP1",
        }),
      );
    }
    if (take_profit_2 !== undefined && take_profit_2 !== null) {
      lines.push(
        series.createPriceLine({
          price: take_profit_2,
          color: accent,
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: true,
          title: "TP2",
        }),
      );
    }
    return () => {
      lines.forEach((l) => series.removePriceLine(l));
    };
  }, [direction, entry, stop_loss, take_profit_1, take_profit_2]);

  // Signal markers
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series || !markers) return;
    const arr = markers
      .filter((m) => m.time)
      .map((m) => ({
        time: Math.floor(new Date(m.time).getTime() / 1000),
        position: m.direction === "short" ? "aboveBar" : "belowBar",
        color: m.direction === "short" ? "#FF3B30" : "#00FF66",
        shape: m.direction === "short" ? "arrowDown" : "arrowUp",
        text: m.text || `${m.direction?.toUpperCase() || ""}${m.score ? " " + m.score : ""}`,
        size: 1.2,
      }));
    try {
      series.setMarkers(arr);
    } catch (_) {
      /* older lightweight-charts */
    }
  }, [markers]);

  return (
    <div
      ref={containerRef}
      data-testid="trading-chart"
      style={{ width: "100%", height: `${height}px` }}
      className="border border-white/10 bg-[#0A0A0A]"
    />
  );
});
